import { useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts';
import dayjs from 'dayjs';
import { SIPEntry } from '../../../../types/investmentLog';
import { FinancialGoal } from '../../../../domain/FinancialGoals';
import { InvestmentSuggestion } from '../../../../types/planner';
import { buildPortfolioWithdrawalSeries, GoalSuggestion } from '../../../../domain/investmentLog';
import { formatCompactCurrency } from '../../../../types/util';

type Props = {
  sips: SIPEntry[];
  goals: FinancialGoal[];
  allSuggestions: InvestmentSuggestion[];
  goalWiseSuggestions?: GoalSuggestion[];
};

const GoalGrowthChart = ({ sips, goals, allSuggestions, goalWiseSuggestions }: Props) => {
  const theme = useTheme();
  const [stopPerGoal, setStopPerGoal] = useState(false);

  const { points, suggestedPoints, goalMarkers } = useMemo(
    () => buildPortfolioWithdrawalSeries(
      sips, goals, allSuggestions,
      stopPerGoal ? goalWiseSuggestions : undefined,
    ),
    [sips, goals, allSuggestions, goalWiseSuggestions, stopPerGoal],
  );

  if (points.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }} aria-label="Portfolio growth projection chart">
        <Typography variant="body2" color="text.secondary">
          {sips.length === 0
            ? 'Log your SIPs in the Investment Plan tab to see portfolio growth.'
            : 'Add one-time financial goals to see growth with withdrawals.'}
        </Typography>
      </Box>
    );
  }

  const goesNegative = points.some((p) => p.value < 0);
  const shortfalls = goalMarkers.filter((m) => m.portfolioValueAfter < 0);

  const todayDate = dayjs().startOf('month').toDate();
  const showTodayLine = todayDate >= points[0].date && todayDate <= points[points.length - 1].date;

  return (
    <Box aria-label="Portfolio growth projection chart">
      {goalWiseSuggestions && goalWiseSuggestions.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ToggleButtonGroup
            value={stopPerGoal ? 'stop' : 'continue'}
            exclusive
            onChange={(_, v) => { if (v !== null) setStopPerGoal(v === 'stop'); }}
            size="small"
          >
            <ToggleButton value="continue" sx={{ fontSize: 11, py: 0.4, px: 1.5, textTransform: 'none' }}>
              Keep investing
            </ToggleButton>
            <ToggleButton value="stop" sx={{ fontSize: 11, py: 0.4, px: 1.5, textTransform: 'none' }}>
              Stop per goal
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <LineChart
        xAxis={[
          {
            data: points.map((p) => p.date),
            scaleType: 'time',
            valueFormatter: (value: Date) => dayjs(value).format('MMM YYYY'),
            tickInterval: (value: Date) => value.getMonth() === 0,
          },
        ]}
        yAxis={[{ valueFormatter: (value: number) => formatCompactCurrency(value) }]}
        series={[
          ...(suggestedPoints.length > 0 ? [{
            data: suggestedPoints.map((p) => p.value),
            label: 'If on plan',
            color: theme.palette.warning.main,
            area: false,
            curve: 'linear' as const,
            showMark: false,
          }] : []),
          {
            data: points.map((p) => p.value),
            label: 'Actual',
            color: theme.palette.primary.main,
            area: true,
            curve: 'linear' as const,
            showMark: false,
          },
        ]}
        height={380}
        sx={{ '& .MuiAreaElement-root': { fillOpacity: 0.12 } }}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
            padding: 0,
            itemMarkWidth: 16,
            itemMarkHeight: 3,
            markGap: 6,
          },
        }}
        margin={{ top: 60, left: 70, right: 30, bottom: 60 }}
      >
        {showTodayLine && (
          <ChartsReferenceLine
            x={todayDate}
            label="Today"
            labelAlign="start"
            lineStyle={{ strokeDasharray: '4 4', stroke: '#666', strokeWidth: 1.5 }}
            labelStyle={{ fontSize: 11, fill: '#666' }}
          />
        )}
        {goesNegative && (
          <ChartsReferenceLine
            y={0}
            label="Portfolio exhausted"
            labelAlign="end"
            lineStyle={{ stroke: theme.palette.error.main, strokeWidth: 1.5 }}
            labelStyle={{ fontSize: 11, fill: theme.palette.error.main, fontWeight: 600 }}
          />
        )}
        {goalMarkers.map((marker, i) => (
          <ChartsReferenceLine
            key={i}
            x={marker.date}
            label={`${marker.goalName} (${formatCompactCurrency(marker.amount)})`}
            labelAlign="start"
            lineStyle={{
              stroke: theme.palette.info.main,
              strokeWidth: 1.5,
              strokeDasharray: '4 4',
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            labelStyle={{ fontSize: 9, fill: theme.palette.info.main, textAnchor: 'end', transform: 'rotate(-45deg)', transformBox: 'fill-box', transformOrigin: 'right bottom' } as any}
          />
        ))}
      </LineChart>

      {shortfalls.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5, px: 1 }}>
          {shortfalls.map((m, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.light',
                backgroundColor: (t) => `${t.palette.error.main}10`,
              }}
            >
              <Typography variant="caption" color="error.main" fontWeight={600}>
                ⚠
              </Typography>
              <Typography variant="caption" color="error.main">
                <strong>{m.goalName}</strong> ({formatCompactCurrency(m.amount)}) — portfolio short by{' '}
                <strong>{formatCompactCurrency(Math.abs(m.portfolioValueAfter))}</strong> at target date.
                Increase your SIPs or push the goal timeline out.
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GoalGrowthChart;
